import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mapIntentHandlers } from '@/features/map/prefetchMap';
import type { TFunction } from 'i18next';
import type { LiveVehicle } from '@/features/live/types';
import { hasPosition } from '@/features/live/motion';
import { layoutControlColumn } from './mapControlOffset';

/**
 * Canvas 1i's embedded "Live fleet · Open full map ›" panel, on the Overview.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MAPLIBRE MUST NEVER ENTER THE ENTRY CHUNK. This is the hard blocker (M-1).
 * ─────────────────────────────────────────────────────────────────────────────
 * `Dashboard` is EAGERLY imported by the router, so it lives in the entry chunk —
 * the login critical path. maplibre-gl is ~230 kB gzipped. So two things hold, by
 * construction, not by luck:
 *
 *   1. There is NO static import of MapCanvas (or anything that pulls maplibre)
 *      anywhere on this module's static graph — only `import type`. The map is
 *      reached solely through the dynamic `import()` in `lazy()` below, which Vite
 *      splits into its own async chunk. So `grep maplibre` over the built entry
 *      chunk is 0 (M-1), and the chunk is the SAME one `/map` already loads —
 *      MapCanvas is shared, never duplicated.
 *
 *   2. The dynamic `import()` does NOT fire at module scope, nor when this
 *      component mounts. It fires only when the panel scrolls into view, via an
 *      IntersectionObserver flipping `visible` true — the point at which `<MapCanvas>`
 *      is first rendered and React calls the lazy factory. On the landing screen,
 *      below the fold, the map chunk is never requested until the user scrolls to
 *      it (M-4). The real user is a fleet manager on a Mogadishu mobile network;
 *      the map's bytes must not sit on the path between them and their fleet.
 *
 * NO IntersectionObserver (jsdom, or a browser old enough to lack it) → we mount
 * NOTHING and leave the placeholder standing, rather than eagerly loading 230 kB
 * on a device we already know is constrained. Every real Android WebView has had
 * IntersectionObserver since 2016; the "Open full map ›" link is the opt-in for
 * anything older. A perpetual spinner would read as broken, so the resting state
 * is a neutral surface, never a spinner that cannot resolve.
 */

// The dynamic import — split into its own chunk by Vite, SHARED with the /map
// route (same specifier → one chunk). Referenced but not called until a
// `<MapCanvas>` is actually rendered, which only happens once `visible` is true.
const MapCanvas = lazy(() => import('@/features/live/MapCanvas'));

// How far ahead of the viewport to begin fetching the chunk, so the map is ready
// (or nearly) by the time it is scrolled into view — without being so eager that
// it fires on the landing paint. Kept well under the below-the-fold gap so the
// panel genuinely lazy-loads on scroll and never on the login critical path
// (asserted at 390×844 by ci/check-overview-render.mjs, where the panel sits
// comfortably more than this margin below the fold).
const PREFETCH_MARGIN = '48px';

function MapLoading({ t }: { t: TFunction }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-raad-inset" aria-hidden="true">
      <span className="size-7 animate-spin rounded-full border-2 border-raad-line-15 border-t-raad-accent" />
      <span className="sr-only" role="status">
        {t('dashboard.map.loading')}
      </span>
    </div>
  );
}

export function OverviewLiveMap({
  vehicles,
  lowData,
  t,
}: {
  vehicles: LiveVehicle[];
  lowData: boolean;
  t: TFunction;
}) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  // 56f review — the street-tile recovery's give-up notice. Without it a PERMANENT
  // tile outage left this card silently white (Live map already showed the notice).
  const [tilesFellBack, setTilesFellBack] = useState(false);

  useEffect(() => {
    if (visible) return; // once loaded, stay loaded — the observer's job is done
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: PREFETCH_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  // 🔴 SLICE 56c/56d — THE ATTRIBUTION AND THE CONTROL COLUMN FOLLOW THE CARD, not a
  // fixed nav lift (Mahad, 20–21 Sep, with screenshots). The embedded map is a SCROLLING
  // card under the fixed phone nav. Slice 56b lifted the attribution by the nav's whole
  // height always, which put the Google logo + imagery line MID-CARD whenever the card
  // was fully in view. The right rule: an element sits a small inset from the CARD's
  // bottom and scrolls with it, lifting ONLY by however much the nav actually overlaps
  // the card — `overlap = max(0, cardRect.bottom − navRect.top)`. So it clings to the
  // card's bottom until the card scrolls under the nav, then rides just above the nav.
  //
  // 🔴 56d — the CONTROL COLUMN (Layers/±/fit/fences) had the SAME defect the 56c
  // attribution fix cured: anchored at a fixed `bottom-3`, its lowest button scrolled
  // UNDER the nav and landed on "More" (reproduced at a bottom safe-area inset: the
  // control gate measured an 18px overlap at inset=34). It is the same class of bug —
  // a control positioned against the map instead of against what is visible — so it
  // takes the same cure: `--raad-map-ctrl-b = 12px + overlap` (12px = the old
  // `bottom-3`), which the compact `MapControls` column reads as its `bottom`.
  //
  // Both vars are recomputed on scroll/resize (rAF-throttled, passive) and on mount /
  // sheet-height change (`visible`). `--raad-map-attrib-b` the `.maplibregl-ctrl-bottom-*`
  // CSS + Google overlay read; `--raad-map-ctrl-b` the control column reads. No bottom
  // nav (desktop) → overlap 0 → the plain 8px / 12px insets.
  useEffect(() => {
    const card = containerRef.current;
    if (!card) return;
    let raf = 0;
    const recompute = () => {
      raf = 0;
      const nav = document.querySelector('[data-testid="mobile-tabbar"]');
      const navRect = nav?.getBoundingClientRect();
      const navTop = navRect && navRect.height > 0 ? navRect.top : Number.POSITIVE_INFINITY;
      const overlap = Math.max(0, card.getBoundingClientRect().bottom - navTop);
      card.style.setProperty('--raad-map-attrib-b', `${Math.round(8 + overlap)}px`);
      // 🔴 56g — CLAMP THE CONTROL-COLUMN LIFT. `12 + overlap` had no ceiling, so a
      // card scrolled far under the nav pushed the column's TOP over the "Open full
      // map" header, out of the map surface. Bound it by the surface height minus the
      // column's own height so the top never crosses the surface top. The surface is
      // the card's map area (the child below the header); the column is the controls.
      // Before the map mounts these measure 0 and the base (12 + overlap) is used —
      // the pre-JS behaviour — and the next rAF corrects it once they render.
      // (verifier) Laid out against the VISIBLE band, not the surface's full height:
      // when the full column does not fit between the surface top and the nav, show
      // the essentials only (`data-ctrl-compact` hides `[data-ctrl-optional]`), so the
      // column neither covers the nav nor rises over the header. See
      // mapControlOffset.ts `layoutControlColumn`.
      const surface = card.querySelector<HTMLElement>('[data-raad-map-surface]');
      const controls = card.querySelector<HTMLElement>('[data-testid="map-controls"]');
      // Button height from the FIRST (always-shown) button; counts from the markup:
      // `data-ctrl-optional` (fit, zones) are shed first, `data-ctrl-secondary` (+, -) next.
      const first = controls?.querySelector<HTMLElement>('button');
      const all = controls ? controls.querySelectorAll('button').length : 0;
      const optional = controls ? controls.querySelectorAll('[data-ctrl-optional]').length : 0;
      const secondary = controls ? controls.querySelectorAll('[data-ctrl-secondary]').length : 0;
      const layout = layoutControlColumn({
        overlap,
        surfaceTop: surface ? surface.getBoundingClientRect().top : 0,
        cardBottom: card.getBoundingClientRect().bottom,
        navTop,
        buttonHeight: first ? first.offsetHeight : 0,
        gap: 8,
        counts: { full: all, compact: all - optional, minimal: all - optional - secondary },
      });
      card.style.setProperty('--raad-map-ctrl-b', `${Math.round(layout.bottom)}px`);
      card.setAttribute('data-ctrl-level', layout.level);
    };
    const onScrollResize = () => {
      if (raf) return; // rAF-throttle — at most one recompute per frame
      raf = requestAnimationFrame(recompute);
    };
    recompute();
    // Passive: we only measure, never preventDefault — so scrolling stays smooth on
    // the Android phone this is built for.
    window.addEventListener('scroll', onScrollResize, { passive: true });
    window.addEventListener('resize', onScrollResize, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollResize);
      window.removeEventListener('resize', onScrollResize);
    };
    // Re-run when the map mounts (`visible`), so the var is right the instant the
    // attribution control appears.
  }, [visible]);

  // Only LOCATED vehicles get markers — a vehicle with no cached position is not a
  // point on a map, exactly as the full live map treats it.
  const located = vehicles.filter(hasPosition);

  // A bordered CARD with the header INSIDE it (canvas 1i draws "Live fleet" as one
  // continuous panel, header row then map). `data-testid="overview-map"` is on the
  // WHOLE card, not just the map surface: it is the element the canvas gate sizes
  // against the alerts card (equal height, one row), and the GL canvas lives inside
  // it, so the render gate's clientHeight/canvas checks hold either way. On lg the
  // card fills the row height; below lg it is a fixed-height card that stacks.
  return (
    <div
      ref={containerRef}
      data-testid="overview-map"
      // 🔴 SLICE 56c — `--raad-map-attrib-b` is now driven by the scroll effect above
      // (attribution follows the card, lifted only by the nav's actual overlap). The
      // `8px` here is only the pre-JS fallback (SSR / before the first rAF); the
      // attribution CSS (`.maplibregl-ctrl-bottom-*`) and the Google overlay both read
      // the var. See the effect for why the fixed nav-height lift was wrong.
      // 🔴 56g — ON THE PHONE THE MAP FILLS THE FIRST SCREEN, down to the fixed
      // bottom nav (Mahad's "compact stats + map" layout). The height is the viewport
      // minus the shell chrome (`--raad-chrome`, published by AppShell), the device
      // safe-area, and a fixed allowance for the search + chip rows above it and the
      // nav clearance below — so the card's bottom lands just above the tab bar and
      // never under it (asserted by check-overview-render.mjs and
      // check-map-attribution.mjs §56d). `lg:h-full` restores the desktop behaviour:
      // the card fills its grid row beside the alerts rail, untouched.
      className="flex h-[calc(100dvh-var(--raad-chrome,64px)-var(--raad-safe-b,0px)-15rem)] flex-col overflow-hidden rounded-panel border border-raad-border bg-raad-panel [--raad-map-attrib-b:8px] lg:h-full"
    >
      <div className="flex flex-none items-center justify-between gap-3 border-b border-raad-border px-4 py-3">
        <h2 className="font-display text-sm font-bold text-raad-text">{t('dashboard.map.heading')}</h2>
        <Link to="/map" {...mapIntentHandlers} className="text-xs font-semibold text-raad-accent hover:underline">
          {t('dashboard.map.openFull')} <span aria-hidden="true">›</span>
        </Link>
      </div>
      {/* `relative` + `flex-1`: MapCanvas paints into an `absolute inset-0` child,
          so this box owns the height. A zero-height container renders a map clipped
          to nothing while every GL probe reports health (the slice-10a shape) — so
          the surface is a real flex child that fills the card below the header. */}
      <div className="relative min-h-0 flex-1 bg-raad-inset" data-raad-map-surface>
        {tilesFellBack && !lowData ? (
          <p
            role="status"
            className="pointer-events-none absolute inset-x-3 top-3 z-10 rounded-lg border border-raad-warn/40 bg-raad-warn/15 px-3 py-2 text-xs text-raad-text"
          >
            {t('livemap.tilesOffline')}
          </p>
        ) : null}
        {visible ? (
          <Suspense fallback={<MapLoading t={t} />}>
            <MapCanvas
              vehicles={located}
              selectedId={null}
              // Tapping a marker opens that vehicle — the same destination the
              // fleet list rows use, so the two glance surfaces agree.
              onSelect={(id) => navigate(`/vehicles/${id}`)}
              following={false}
              lowData={lowData}
              // 51b — this card is short (h-72) and has no bottom sheet, so the
              // control column uses the compact bottom-right anchor instead of the
              // live map's 184px sheet-clearance offset.
              compactControls
              // 56g — the Overview map is LIVE and RE-FRAMES when a vehicle moves out
              // of view (suppressed after a user gesture). Only this embedded map opts
              // in; the full Live map's camera behaviour is unchanged.
              autoFrame
              onTilesFallback={() => setTilesFellBack(true)}
              t={t}
            />
          </Suspense>
        ) : (
          // The resting state before the panel is scrolled into view. Neutral, not
          // a spinner — the map has not been asked for yet, and saying "loading"
          // before that would be a claim about work that is not happening.
          <div className="absolute inset-0 bg-raad-inset" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}

export default OverviewLiveMap;
